import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import employee from '../class/employee';
import warehouse from '../class/warehouse';
import swal from 'sweetalert';
import "../styles/DischargePage.css";

const DischargePage = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [items, setItems] = useState([]);
    const [dischargeDate, setDischargeDate] = useState(() => {
        const now = new Date();
        now.setMinutes(now.getMinutes() - now.getTimezoneOffset());
        return now.toISOString().slice(0, 16);
    });
    const [ddtNumber, setDdtNumber] = useState('');
    const [loading, setLoading] = useState(false);
    const [userInfo, setUserInfo] = useState(null);

    useEffect(() => {
        let user_obj = new employee();
        user_obj.isLogged().then(() => {
            if (!location.state || !location.state.selectedProducts || location.state.selectedProducts.length === 0) {
                navigate('/products');
                return;
            }

            const storedUserInfo = location.state.userInfo || user_obj.getUserInfo();
            setUserInfo(storedUserInfo);

            // Inizializza items con quantità disponibile come default
            const initialItems = location.state.selectedProducts.map(p => ({
                ...p,
                discharge_quantity: p.available_quantity
            }));
            setItems(initialItems);
        }).catch(() => {
            navigate('/login');
        });
    }, []);

    const handleQuantityChange = (order_uuid, value) => {
        setItems(prev => prev.map(item => {
            if (item.order_uuid === order_uuid) {
                return { ...item, discharge_quantity: value };
            }
            return item;
        }));
    };

    const handleRemoveItem = (order_uuid) => {
        setItems(prev => {
            const updated = prev.filter(item => item.order_uuid !== order_uuid);
            if (updated.length === 0) {
                navigate('/products');
            }
            return updated;
        });
    };

    const formatNumber = (value) => {
        if (value == null || isNaN(value)) return "0,00000";
        return Number(value).toLocaleString('it-IT', {
            minimumFractionDigits: 5,
            maximumFractionDigits: 5,
        });
    };

    const handleSubmit = async () => {
        // Validazione
        if (!dischargeDate) {
            swal("Attenzione", "Inserire la data di scarico", "warning");
            return;
        }
        if (!ddtNumber.trim()) {
            swal("Attenzione", "Inserire il numero DDT", "warning");
            return;
        }

        const invalidItems = items.filter(item => {
            const qty = Number(item.discharge_quantity);
            return !qty || qty <= 0;
        });
        if (invalidItems.length > 0) {
            swal("Attenzione", "Tutte le quantità devono essere maggiori di 0", "warning");
            return;
        }

        const overItems = items.filter(item => {
            const qty = Number(item.discharge_quantity);
            return qty > Number(item.available_quantity);
        });
        if (overItems.length > 0) {
            swal("Attenzione", "La quantità non può superare la disponibilità", "warning");
            return;
        }

        // Conferma
        const confirm = await swal({
            title: "Conferma Scarico",
            text: `Procedere con lo scarico di ${items.length} prodott${items.length === 1 ? 'o' : 'i'} con DDT ${ddtNumber}?`,
            icon: "info",
            buttons: ["Annulla", "Conferma"],
        });

        if (!confirm) return;

        setLoading(true);
        try {
            const warehouseWS = new warehouse();
            const scarico_items = items.map(item => ({
                order_uuid: item.order_uuid,
                quantity: Number(item.discharge_quantity)
            }));

            // Converti data in Unix timestamp (secondi)
            const dateTimestamp = Math.floor(new Date(dischargeDate).getTime() / 1000);

            await warehouseWS.saveScarico({
                discharge_date: dateTimestamp,
                employee_uuid: userInfo.uuid,
                ddt_number: ddtNumber,
                items: scarico_items
            });

            swal("Successo", "Scarico effettuato con successo", "success").then(() => {
                navigate('/products');
            });
        } catch (error) {
            swal("Errore", error.toString(), "error");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="discharge-page">
            <div className="detail-header">
                <button onClick={() => navigate('/products')} className="btn-indietro">← Indietro</button>
                <h2 className="page-title">Scarico Prodotti</h2>
            </div>

            <div className="discharge-form">
                <div className="form-row">
                    <div className="form-group">
                        <label htmlFor="dischargeDate">Data e Ora Scarico:</label>
                        <input
                            type="datetime-local"
                            id="dischargeDate"
                            value={dischargeDate}
                            onChange={(e) => setDischargeDate(e.target.value)}
                        />
                    </div>
                    <div className="form-group">
                        <label htmlFor="ddtNumber">Numero DDT:</label>
                        <input
                            type="text"
                            id="ddtNumber"
                            value={ddtNumber}
                            onChange={(e) => setDdtNumber(e.target.value)}
                            placeholder="Inserisci numero DDT"
                        />
                    </div>
                </div>
            </div>

            <div className="discharge-table-container">
                <table>
                    <thead>
                        <tr className='table-head' style={{ backgroundColor: '#003049', color: '#fff', fontWeight: 'bold' }}>
                            <th>N. Ordine Produzione</th>
                            <th>Descrizione Articolo</th>
                            <th>Lotto</th>
                            <th>UM</th>
                            <th>Qtà Disponibile</th>
                            <th>Qtà da Scaricare</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {items.map(item => (
                            <tr key={item.order_uuid}>
                                <td>{item.order_production_number}</td>
                                <td>{item.article_descr}</td>
                                <td>{item.lot}</td>
                                <td>{item.um}</td>
                                <td>{formatNumber(item.available_quantity)}</td>
                                <td>
                                    <input
                                        type="number"
                                        className="qty-input"
                                        value={item.discharge_quantity}
                                        onChange={(e) => handleQuantityChange(item.order_uuid, e.target.value)}
                                        min="1"
                                        max={item.available_quantity}
                                        placeholder="0"
                                    />
                                </td>
                                <td>
                                    <button
                                        className="btn-remove"
                                        onClick={() => handleRemoveItem(item.order_uuid)}
                                        title="Rimuovi"
                                    >
                                        ✕
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <div className="discharge-submit">
                <button
                    className="blue_butt confirm-btn"
                    onClick={handleSubmit}
                    disabled={loading}
                >
                    {loading ? "Elaborazione..." : "Conferma Scarico"}
                </button>
            </div>
        </div>
    );
};

export default DischargePage;
